/**
 * Copyright (C) IRT GmbH 2020
 */

var EventEmitter = require('events');

var _KeyEvent = require('../tools/keyevent_constants');

var createElement = require('../tools/functions').createElement;

/**
 * @typedef FAQ
 * @property {string} question
 * @property {string} answer
 */


/**
 * FAQPage allows to render a list of questions and answers as a full screen HbbTV page
 * using key events for navigation. The page is invisible after creation, use show to make visible
 *
 * usage:
 *      var faqPage = FAQPage(node1, "The FAQs", "Close page", [ { question : "Who are you?", answer : "I don't know." } ]);
 *      faqPage.show(true);
 *
 * @returns {FAQPage}
 *
 * @param {Element} domelem Parent HTMLElement used to render the FAQPage
 * @param {string} title A title for the page
 * @param {string} closeText Label for the close button
 * @param {Array<FAQ>} faqs An array of FAQ elements consisting of questions and answers
 */
function FAQPage(domelem, opt) {

    var emitter = new EventEmitter();

    if (!domelem || !opt.questions)
        throw "FAQPage: Invalid arguments";

    var faqPage = createElement(domelem, "div", "faqPage");
    createElement(faqPage, "div", "faqTitle", opt.title);
    var faqArea = createElement(faqPage, "div", "faqArea focussed");

    var selected = -1;
    var faqList = [];
    for (x in opt.questions) {
        faqList.push(faqArea.appendChild(createFAQNode(opt.questions[x])));
    }

    var closeButton = createElement(faqPage, "a", "clickButton", opt.closeButton);

    show(false);

    /**
     * @param {boolean} visible make FAQ elem visible or hidden
     */
    function show (visible) {
        if (faqPage) {
            faqPage.style.display = visible ? "block" : "none";
            document[visible ? "addEventListener" : "removeEventListener"]("keydown", keyhandler, false);

            if (visible) {
                if (faqList.length > 0) {
                    faqList[0].className += " selected";
                    selected = 0;
                }
                selectClose(selected === -1);
            } else {
            }
        }
    }

    function keyhandler (e) {
        switch (e.keyCode) {
            case e.VK_UP: case _KeyEvent.VK_UP:
                up();
                break;
            case e.VK_DOWN: case _KeyEvent.VK_DOWN:
                down();
                break;
            case e.VK_LEFT: case _KeyEvent.VK_LEFT:
                left();
                break;
            case e.VK_RIGHT: case _KeyEvent.VK_RIGHT:
                right();
                break;
            case e.VK_ENTER: case _KeyEvent.VK_ENTER:
                onEnter();
                break;
            case e.VK_BACK: case _KeyEvent.VK_BACK:
                right();
                onEnter();
                break;
            default:
                return;
        }
        e.preventDefault();
    }

    function onEnter() {
        if (selected === -1) {
            show(false);
            emitter.emit('close');
        }
    }

    function select(newSelected) {
        if (selected > -1) {
            faqList[selected].classList.remove("selected");
        }
        if (newSelected > -1) {
            faqList[newSelected].classList.add("selected");
            faqList[newSelected].scrollIntoView({behavior: "smooth", block: "center"});
            faqArea.classList.add("focussed");
        } else {
            faqArea.classList.remove("focussed");
        }
        selected = newSelected;
    }

    function selectClose (b) {
        if (b) {
            closeButton.classList.add("selected");
        } else {
            closeButton.classList.remove("selected");
        }
    }

    function down () {
        if (selected == faqList.length - 1) {
            select(-1);
            selectClose(true);
        } else if (selected > -1) {
            select(selected + 1);
        } else {

        }
    }

    function up () {
        if (selected == 0) {

        } else if (selected > 0) {
            select(selected - 1);
        } else {
            selectClose(false);
            select(faqList.length - 1);
        }
    }

    function right() {
        if (selected > -1) {
            select(-1);
            selectClose(true);
        }
    }

    function left() {
        if (selected == -1) {
            selectClose(false);
            select(faqList.length - 1);
        }
    }

    /**
     * Creates a new faq node with a question and answer node.
     *
     * @param {FAQ} faq
     * @returns {Element} the new dom element
     */
    function createFAQNode(faq) {
        var faqNode = document.createElement("div");
        faqNode.setAttribute("class", "faqNode");

        createElement(faqNode, "div", "faqQuestionNode", faq.question);
        var faqA = createElement(faqNode, "div", "faqAnswerNode", faq.answer);

        if (faq.image) {
            var img = faqA.appendChild(document.createElement("img"));
            img.setAttribute("src", faq.image);
        }

        return faqNode;
    }

    return {
        /**
         * Shows and hides the FAQPage
         */
        show : show,
        on : emitter.on.bind(emitter),
        off : emitter.removeListener.bind(emitter),
        once : emitter.once.bind(emitter)
    };
}

module.exports = FAQPage;
